import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { map, catchError, of } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  if (authService.hasRedirectHash() || authService.isProcessingRedirect()) {
    return true;
  }

  // This is the core of the fix. Instead of checking authentication immediately,
  // we return an Observable. The router (thanks to withEnabledBlockingInitialNavigation)
  // will pause the navigation and wait for this Observable to produce a value.
  return authService.waitForMsalInitialization().pipe(
    map(() => {
      // This `map` block will ONLY execute after `waitForMsalInitialization` emits `true`.
      // At this point, we are 100% certain that the AuthenticationService has checked for
      // a cached user session, so `isUserAuthenticated()` will give a reliable answer.
      if (authService.isUserAuthenticated()) {
        if (authService.isUserAuthorized(route.data)) {
          return true; // User is authenticated and authorized.
        } else {
          // User is authenticated but not authorized for this route.
          router.navigate(['/dashboard']); // Or an 'unauthorized' page.
          return false;
        }
      } else {
        // User is not authenticated, redirect to login.
        router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return false;
      }
    }),
    catchError(() => {
      // If MSAL initialization fails, redirect to login.
      router.navigate(['/login']);
      return of(false);
    }),
  );
};
